import { useEffect, useState } from "react";
import { API_BASE } from "../../config/apiConfig";
import ActivityDropdown from "../../components/activityDropdown";
import PageHeader from "../../components/pageHeader";
import { useToast } from "../../hooks/useToast";
import LoadingOverlay from "../../components/loadingOverlay";
import MainContentLayout from "../../components/mainContentLayout";
import { FaCheck, FaTimes } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

export default function AddResultPage() {
  const token = sessionStorage.getItem("access_token");
  const navigate = useNavigate();

  const [players, setPlayers] = useState([]);
  const [activities, setActivities] = useState([]);

  const [selectedPlayer, setSelectedPlayer] = useState("");
  const [selectedActivity, setSelectedActivity] = useState("");

  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    playerId: "",
    playerName: "",
    tournamentId: "",
    tournamentName: "",
    ageGroup: "",
    rank: "",
    placement: "",
    resultStatus: "Normal",
    medal: "",
    rankingType: "",
    year: "",
  });

  const { showToast, ToastContainer } = useToast();

  // ------------------------------------------------------------
  // LOAD PLAYERS + ACTIVITIES
  // ------------------------------------------------------------
  useEffect(() => {
    loadPlayers();
    loadActivities();
  }, []);

  async function loadPlayers() {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/performance-players`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        setPlayers(data.players);
      }
    } finally {
      setLoading(false);
    }
  }

  async function loadActivities() {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/performance-activities`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        setActivities(data.activities);
      }
    } finally {
      setLoading(false);
    }
  }

  // ------------------------------------------------------------
  // FORM HANDLING
  // ------------------------------------------------------------
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "rank") {
      let medal = null;
      if (parseInt(value) === 1) medal = "Gold";
      else if (parseInt(value) === 2) medal = "Silver";
      else if (parseInt(value) === 3) medal = "Bronze";

      setForm((prev) => ({
        ...prev,
        [name]: value,
        medal,
        placement: "",
        rankNote: "",
        resultStatus: "Normal",
      }));
    } else if (name === "placement") {
      setForm((prev) => ({
        ...prev,
        [name]: value,
        rank: "",
        rankNote: "",
        resultStatus: "Normal",
      }));
    } else if (name === "resultStatus") {
      setForm((prev) => ({
        ...prev,
        [name]: value,
        rank: "",
        placement: "",
        rankNote: "",
      }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handlePlayerChange = (e) => {
    const pid = e.target.value;
    const player = players.find((p) => p.id === pid);

    setSelectedPlayer(pid);
    setForm((prev) => ({
      ...prev,
      playerId: player.id,
      playerName: player.fullName,
    }));
  };

  const handleActivitySelect = (actId) => {
    const act = activities.find((a) => a.id === actId);
    if (!act) return;

    const startDate = act.startDate?._seconds
      ? new Date(act.startDate._seconds * 1000)
      : new Date(act.startDate);

    const year = startDate.getFullYear();

    setSelectedActivity(actId);

    setForm((prev) => ({
      ...prev,
      tournamentId: act.id,
      tournamentName: act.name,
      rankingType: act.rankingType,
      year,
    }));
  };

  // ------------------------------------------------------------
  // SUBMIT
  // ------------------------------------------------------------
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await fetch(`${API_BASE}/api/performance-add-result`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(form),
      });

      const data = await res.json();
      if (data.success) {
        showToast("Result added successfully!", "success");
      }
    } finally {
      setLoading(false);
    }
  };

  // ------------------------------------------------------------
  // UI
  // ------------------------------------------------------------
  return (
    <MainContentLayout>
      <LoadingOverlay show={loading} />
      <PageHeader
        title="Add New Result"
        backTo="/admin-performance-review-landing"
      />

      <form onSubmit={handleSubmit} className="max-w-2xl mx-auto w-full space-y-4 text-left mb-4">
        {/* Buttons */}
        <div className="flex justify-end gap-2">
          <button
            type="submit"
            className="bg-green-600 hover:bg-green-800 p-1 rounded text-white transition"
            title="Submit"
          >
            <FaCheck size={20} />
          </button>
          <button
            type="button"
            onClick={() => navigate("/admin-performance-review-landing")}
            className="p-1 bg-gray-300 rounded"
            title="Cancel"
          >
            <FaTimes size={20} />
          </button>
        </div>

        {/* Player dropdown */}
        <div>
          <label className="block mb-1 font-medium">
            Player <span className="text-red-500 italic text-xs">*Required</span>
          </label>
          <select
            name="playerId"
            value={selectedPlayer}
            onChange={handlePlayerChange}
            className="w-full border rounded p-2"
            required
          >
            <option value="">Select Player</option>
            {players.map((p) => (
              <option key={p.id} value={p.id}>{p.fullName}</option>
            ))}
          </select>
        </div>

        {/* Activity dropdown */}
        <div>
          <label className="block mb-1 font-medium">
            Tournament / Activity <span className="text-red-500 italic text-xs">*Required</span>
          </label>
          <ActivityDropdown
            activities={activities}
            selectedActivity={selectedActivity}
            setSelectedActivity={handleActivitySelect}
          />
        </div>

        {/* Age Group */}
        <div>
          <label className="block mb-1 font-medium">
            Age Group <span className="text-red-500 italic text-xs">*Required</span>
          </label>
          <select
            name="ageGroup"
            value={form.ageGroup}
            onChange={handleChange}
            className="w-full border rounded p-2"
            required
          >
            <option value="">Select Age Group</option>
            {["BU9","GU9","BU11","GU11","BU13","GU13","BU15","GU15","BU17","GU17"]
              .map((g) => <option key={g} value={g}>{g}</option>)
            }
          </select>
        </div>

        <div className="space-y-2">
          {/* Result Status */}
          {!form.rank && !form.placement && (
            <div>
              <label className="mb-1 font-medium flex text-sm items-center gap-1">
                Result Status
                
                {/* Tooltip wrapper */}
                <div className="relative group">
                  {/* Icon */}
                  <div
                    className="w-4 h-4 flex items-center justify-center rounded-full bg-gray-300 text-gray-700 text-xs cursor-pointer"
                  >
                    ?
                  </div>

                  {/* Tooltip on hover */}
                  <div className="absolute mt-2 w-56 p-2 rounded-md text-xs text-white bg-gray-800 opacity-0 group-hover:opacity-100 transition-opacity z-10 pointer-events-none">
                    <span className="font-semibold">Tournament participation status:</span>
                    <ol className="list-decimal ml-4 space-y-1 mt-2">
                      <li>
                        <span className="font-medium">Normal:</span> Player finished the entire
                        tournament as scheduled.
                      </li>
                      <li>
                        <span className="font-medium">Walkover (W/O):</span> Player did not play
                        a scheduled match, usually due to injury or absence, but did not formally
                        withdraw from the event.
                      </li>
                      <li>
                        <span className="font-medium">Withdraw:</span> Player officially pulled
                        out from the tournament before completing all scheduled matches.
                      </li>
                    </ol>
                  </div>
                </div>
              </label>

              <select
                name="resultStatus"
                value={form.resultStatus}
                onChange={handleChange}
                className="w-full border rounded p-2"
              >
                <option value="Normal">Normal</option>
                <option value="W/O">Walkover (W/O)</option>
                <option value="Withdraw">Withdraw</option>
              </select>
            </div>
          )}

          {/* Exact Rank */}
          {!form.placement && form.resultStatus === "Normal" && (
            <div>
              <label className="block mb-1 font-medium">Exact Rank</label>
              <input
                type="number"
                name="rank"
                value={form.rank}
                onChange={handleChange}
                placeholder="Exact Rank (1, 2, 3...)"
                className="w-full border rounded p-2 placeholder:italic placeholder:text-gray-400"
              />
            </div>
          )}

          <div>
            <span className="flex justify-center italic font-semibold space-y-2">OR</span>
          </div>

          {/* Placement */}
          {!form.rank && form.resultStatus === "Normal" && (
            <div>
              <label className="block mb-1 font-medium">Select Placement</label>
              <select
                name="placement"
                value={form.placement}
                onChange={handleChange}
                className="w-full border rounded p-2"
              >
                <option value="">Select placement</option>
                <option value="Top128">Top 128</option>
                <option value="Top64">Top 64</option>
                <option value="Top32">Top 32</option>
                <option value="Top16">Top 16</option>
              </select>
            </div>
          )}

          {/* Rank Note */}
          {!form.rank && form.resultStatus === "Normal" && (
            <div>
              <label className="block mb-1 font-medium">
                Ranking Note <span className="italic text-gray-400 text-xs">(Optional)</span>
              </label>
              <input
                type="text"
                name="rankNote"
                value={form.rankNote || ""}
                onChange={handleChange}
                placeholder="Optional: e.g. 49/50"
                className="w-full border rounded p-2 placeholder:italic placeholder:text-gray-400"
              />
            </div>
          )}
        </div>

        {/* Medal */}
        <div>
          <label className="block mb-1 font-medium">
            Medal <span className="italic text-gray-400 text-xs">(Auto)</span>
          </label>
          <input
            type="text"
            name="medal"
            value={form.medal || ""}
            disabled
            className="w-full border rounded p-2 bg-gray-100"
          />
        </div>

        {/* Year */}
        <div>
          <label className="block mb-1 font-medium">
            Year <span className="italic text-gray-400 text-xs">(Auto)</span>
          </label>
          <input
            type="text"
            name="year"
            value={form.year || ""}
            disabled
            className="w-full border rounded p-2 bg-gray-100"
          />
        </div>
      </form>
      <ToastContainer />
    </MainContentLayout>
  );
}
